import { Fragment, useState } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { DownloadIcon } from "components/icons/Download";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { useModal } from "hooks/useModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import {
  useGetSITPayrollDataQuery,
  useUploadSITPayrollDataMutation,
  useDeleteSITPayrollDataMutation,
  useLazyGetSITPayrollTemplateQuery,
} from "store/apis/stateIncomeTax";
import {
  SITPayrollStateItem,
  SITPayrollSortBy,
  SITSortOrder,
} from "types/Models/stateIncomeTax";
import { BackendError } from "types/utils/error";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { formatAmount } from "utils/foramtAmount";
import { Divider } from "components/design/Divider";
import {
  OnChangeFn,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { formatDate } from "utils/formatDate";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Info } from "components/icons/Info";
import Wind from "components/icons/Wind";
import {
  DnDArea,
  File as UploadedFile,
  UploadLabel,
} from "components/design/UploadFileSection";
import { openLink } from "utils/openLink";

const SORT_ID_MAP: Record<string, SITPayrollSortBy> = {
  state_name: "state",
  gross_pay: "gross_pay",
  percentage_of_total: "percentage_of_total",
  total_employees: "total_employees",
};

const ACCEPTED_FILES = {
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};

const columnHelper = createColumnHelper<SITPayrollStateItem>();

type Props = {
  season: number;
};

const EmptyState = ({ onUpload }: { onUpload: () => void }) => (
  <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-4 t-py-16">
    <Wind className="t-text-text-30" />
    <div className="t-flex t-flex-col t-items-center t-gap-1">
      <span className="t-text-body-sm t-text-text-100">
        No payroll data uploaded
      </span>
      <span className="t-text-body-sm t-text-text-30">
        Upload payroll by state to include it in the apportionment calculation.
      </span>
    </div>
    <Button customType="primary" onClick={onUpload}>
      Upload File
    </Button>
  </div>
);

const UploadPayrollDataModal = ({
  isOpen,
  close,
  season,
  groupId,
  entityId,
}: {
  isOpen: boolean;
  close: () => void;
  season: number;
  groupId: string;
  entityId: string;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { successToast, alertToast } = useToast();
  const [upload, { isLoading }] = useUploadSITPayrollDataMutation();
  const [fetchTemplate, { isLoading: isTemplateDownloading }] =
    useLazyGetSITPayrollTemplateQuery();

  const handleClose = () => {
    setSelectedFile(null);
    close();
  };

  const handleSave = async () => {
    if (!selectedFile) return;
    try {
      await upload({ groupId, entityId, season, file: selectedFile }).unwrap();
      successToast({ message: "Payroll data uploaded successfully." });
      handleClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const { download_url } = await fetchTemplate({
        groupId,
        entityId,
      }).unwrap();
      openLink(download_url);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Upload Payroll Data</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-4">
          <UploadLabel>
            Download the pre-formatted template to ensure your data is properly
            structured. Add your payroll by state to the template and upload it.
          </UploadLabel>
          <div>
            <Button
              size="small"
              isLoading={isTemplateDownloading}
              onClick={handleTemplateDownload}
            >
              <div className="t-flex t-items-center t-gap-1.5">
                <DownloadIcon />
                Download template
              </div>
            </Button>
          </div>
          {selectedFile ? (
            <UploadedFile
              file={selectedFile}
              onCancel={() => setSelectedFile(null)}
              isUploaded
              onDelete={() => setSelectedFile(null)}
            />
          ) : (
            <DnDArea
              onDrop={(files) => setSelectedFile(files[0])}
              accept={ACCEPTED_FILES}
              multiple={false}
              subText="File format: .xlsx, .xls"
            />
          )}
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            onClick={handleSave}
            disabled={!selectedFile || isLoading}
            isLoading={isLoading}
          >
            Save
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

const PayrollMetrics = ({
  stateCount,
  totalGrossPay,
  uploadedAt,
}: {
  stateCount: number;
  totalGrossPay: string;
  uploadedAt: string;
}) => {
  const metrics = [
    {
      key: "total_gross_pay",
      title: "Total Gross Pay",
      value: formatAmount(Number(totalGrossPay)),
    },
    {
      key: "state_count",
      title: "States",
      value: stateCount,
    },
    {
      key: "uploaded_at",
      title: "Last Synced",
      value: formatDate(uploadedAt),
    },
  ];

  return (
    <div className="t-flex t-items-stretch t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-surface t-py-5">
      {metrics.map((metric, index) => (
        <Fragment key={metric.key}>
          {index > 0 && (
            <Divider
              orientation="vertical"
              type="dashed"
              classNames="t-self-stretch"
            />
          )}
          <div className="t-flex t-flex-1 t-flex-col t-gap-2 t-px-5">
            <span className="t-text-body-sm t-text-text-30">
              {metric.title}
            </span>
            <span className="t-text-h3 t-font-semibold t-text-text-100">
              {metric.value}
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
};

const RemoveConfirmModal = ({
  isOpen,
  close,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  close: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => (
  <Modal.Root open={isOpen} onOpenChange={close}>
    <Modal.Content size="small">
      <Modal.Header>
        <Modal.Title>Remove Payroll Data</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <div className="t-text-body-sm">
          Are you sure you want to remove the payroll data? This action cannot
          be undone.
        </div>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button onClick={close} disabled={isLoading}>
            Cancel
          </Button>
        </Modal.RawClose>
        <Button
          customType="danger"
          onClick={onConfirm}
          disabled={isLoading}
          isLoading={isLoading}
        >
          Remove
        </Button>
      </Modal.FooterButtonGroup>
    </Modal.Content>
  </Modal.Root>
);

const PayrollTable = ({
  rows,
  sorting,
  onSortingChange,
}: {
  rows: SITPayrollStateItem[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}) => {
  const columns = [
    columnHelper.accessor("state_code", {
      id: "state_code",
      size: 80,
      enableSorting: false,
      header: () => <HeadCellContent title="Code" />,
      cell: (info) => (
        <span className="t-text-body-sm t-font-semibold t-text-text-60">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("state_name", {
      id: "state_name",
      header: (header) => (
        <HeadCellContent
          title="State"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("gross_pay", {
      id: "gross_pay",
      size: 160,
      header: (header) => (
        <HeadCellContent
          title="Gross Pay"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
          {formatAmount(Number(info.getValue()))}
        </span>
      ),
    }),
    columnHelper.accessor("percentage_of_total", {
      id: "percentage_of_total",
      size: 160,
      header: (header) => (
        <HeadCellContent
          title="% of Total"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
          {parseFloat(info.getValue()).toFixed(2)}%
        </span>
      ),
    }),
    columnHelper.accessor("total_employees", {
      id: "total_employees",
      size: 140,
      header: (header) => (
        <HeadCellContent
          title="Employees"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
          {info.getValue().toLocaleString()}
        </span>
      ),
    }),
  ];

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
  });

  return (
    <Table.Container>
      <Table.Content>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.HeadRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  style={{ width: header.getSize() }}
                  isUpperCase={false}
                  className="t-group"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Cell
                  key={cell.id}
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};

export const SITPayrollData = ({ season }: Props) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const uploadModal = useModal();
  const removeModal = useModal();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [deletePayrollData, { isLoading: isDeleting }] =
    useDeleteSITPayrollDataMutation();

  const sort_by = sorting[0] ? SORT_ID_MAP[sorting[0].id] : undefined;
  const sort_order: SITSortOrder | undefined = sorting[0]
    ? sorting[0].desc
      ? "desc"
      : "asc"
    : undefined;

  const { data, isLoading, isSuccess } = useGetSITPayrollDataQuery(
    { groupId, entityId, season, sort_by, sort_order },
    { skip: !groupId || !entityId }
  );

  const hasData = Boolean(data);

  const handleRemove = async () => {
    try {
      await deletePayrollData({ groupId, entityId, season }).unwrap();
      successToast({ message: "Payroll data removed." });
      removeModal.close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <>
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={!hasData}
      >
        <Async.Empty>
          <EmptyState onUpload={uploadModal.open} />
        </Async.Empty>
        <Async.Success>
          {data && (
            <div className="t-flex t-flex-col t-gap-4">
              <Banner
                type="warning"
                icon={
                  <span className="t-text-text-30 t-h-full t-flex t-items-center">
                    <Info color="currentColor" stroke="1.1" size="16" />
                  </span>
                }
                description="Payroll data uploaded. You can replace or remove it anytime before filing."
                cta={
                  <Button
                    customType="danger_outlined"
                    size="small"
                    onClick={removeModal.open}
                  >
                    Remove Data
                  </Button>
                }
              />
              <PayrollMetrics
                stateCount={data.state_count}
                totalGrossPay={data.total_gross_pay}
                uploadedAt={data.uploaded_at}
              />
              {data.data.length > 0 && (
                <PayrollTable
                  rows={data.data}
                  sorting={sorting}
                  onSortingChange={setSorting}
                />
              )}
            </div>
          )}
        </Async.Success>
      </Async.Root>

      <UploadPayrollDataModal
        isOpen={uploadModal.isOpen}
        close={uploadModal.close}
        season={season}
        groupId={groupId}
        entityId={entityId}
      />

      <RemoveConfirmModal
        isOpen={removeModal.isOpen}
        close={removeModal.close}
        onConfirm={handleRemove}
        isLoading={isDeleting}
      />
    </>
  );
};
